import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { Button, Content, Header, Modal, Title, Toolbar } from '@/ui'
import { useAppRouter, useRoute } from '@/router/navigation'
import { useAuthStore } from '@/stores/auth'
import { usePaymentStore } from '@/stores/payment'
import { PAYMENT_ETRANSFER_EMAIL } from '@/config/paymentContacts'
import { OPERATIONS_EMAIL } from '@/config/supportContacts'
import './SkatePaymentFollowUpModal.css'

export interface SkatePaymentFollowUpModalProps {
  isOpen?: boolean
  followUp?: any
  onDismiss?: () => void
  onResolved?: () => void
}

const paymentEmail = PAYMENT_ETRANSFER_EMAIL
const operationsEmail = OPERATIONS_EMAIL

export function SkatePaymentFollowUpModal ({
  isOpen = false,
  followUp = null,
  onDismiss,
  onResolved
}: SkatePaymentFollowUpModalProps) {
  const lastCityId = useAuthStore((s) => s.userProfile?.lastCityId)
  const singleGamePrice = usePaymentStore((s) => s.SINGLE_GAME_PRICE)
  const route = useRoute()
  const router = useAppRouter()

  const [step, setStep] = useState<'question' | 'unpaid'>('question')
  const [submitting, setSubmitting] = useState(false)

  const formattedDate = (() => {
    const d = followUp?.gameDate
    if (!d) return 'this game'
    return new Date(d + 'T12:00:00').toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  })()

  const paymentsPath = (() => {
    const cityId = followUp?.cityId || route.params.cityId || lastCityId
    return cityId ? `/${cityId}/payments` : null
  })()

  useEffect(() => {
    if (isOpen) {
      setStep('question')
      setSubmitting(false)
    }
  }, [isOpen])

  const handleDismiss = () => {
    onDismiss?.()
  }

  const answerYes = async () => {
    if (!followUp?.id) return
    setSubmitting(true)
    try {
      const via =
        followUp.paymentMethod === 'credit-card'
          ? 'credit-card'
          : followUp.paymentMethod || 'confirmed'
      const result = await useAuthStore.getState().resolveSkatePaymentFollowUp(followUp.id, {
        confirmed: true,
        paidVia: via
      })
      if (result.success) {
        onResolved?.()
      }
    } finally {
      setSubmitting(false)
    }
  }

  const dismissUnpaid = async () => {
    if (!followUp?.id) return
    setSubmitting(true)
    try {
      await useAuthStore.getState().resolveSkatePaymentFollowUp(followUp.id, {
        confirmed: false
      })
      onResolved?.()
    } finally {
      setSubmitting(false)
    }
  }

  const goToPayments = async () => {
    if (paymentsPath) {
      await router.push(paymentsPath)
    }
    await dismissUnpaid()
  }

  return (
    <Modal
      isOpen={isOpen}
      backdropDismiss={false}
      onDidDismiss={handleDismiss}
      className="c-skate-payment-follow-up-modal"
      ariaLabel="Skate payment"
    >
      <Header>
        <Toolbar>
          <Title>Skate payment</Title>
        </Toolbar>
      </Header>

      <Content padding>
        {followUp && step === 'question' ? (
          <div className="modal-body">
            <p className="lead">Did you pay for this skate?</p>
            <div className="game-summary">
              <p className="game-date">{formattedDate}</p>
              {followUp.venue && <p className="game-venue">{followUp.venue}</p>}
            </div>

            <Button expand="block" onClick={answerYes} disabled={submitting}>
              Yes, I paid
            </Button>
            <Button
              expand="block"
              fill="outline"
              color="medium"
              onClick={() => setStep('unpaid')}
              disabled={submitting}
            >
              No, not yet
            </Button>
          </div>
        ) : followUp && step === 'unpaid' ? (
          <div className="modal-body">
            <p className="lead">Please complete payment</p>
            <p className="hint">
              For your skate on <strong>{formattedDate}</strong>
              {followUp.venue && <span> at {followUp.venue}</span>}:
            </p>

            <ul className="instructions">
              <li>
                E-transfer <strong>${singleGamePrice} CAD</strong> to{' '}
                <a href={`mailto:${paymentEmail}`}>{paymentEmail}</a>
              </li>
              {paymentsPath && (
                <li>
                  Pay in the app via{' '}
                  <Link to={paymentsPath}>Payments</Link> (credit card)
                </li>
              )}
              <li>
                Questions? Email{' '}
                <a href={`mailto:${operationsEmail}`}>{operationsEmail}</a>
              </li>
            </ul>

            {paymentsPath && (
              <Button expand="block" onClick={goToPayments}>
                Open Payments
              </Button>
            )}
            <Button
              expand="block"
              fill="outline"
              color="medium"
              onClick={dismissUnpaid}
              disabled={submitting}
            >
              I'll pay soon
            </Button>
          </div>
        ) : null}
      </Content>
    </Modal>
  )
}
